// LegalFooter.tsx
// App Store-required disclosures and legal links

import React from 'react';
import { View, Pressable, StyleSheet, Linking } from 'react-native';
import { Text } from '../../../components/ui';
import { Theme } from '../../../utils/theme';

const TERMS_URL = 'https://www.mentorist.app/termsandconditions/';
const PRIVACY_URL = 'https://www.mentorist.app/privacy-policy/';

interface LegalFooterProps {
  hasTrial: boolean;
  priceString: string;
  period: 'monthly' | 'annual';
  trialDays?: number;
  onRestore: () => void;
  isRestoring?: boolean;
}

export const LegalFooter: React.FC<LegalFooterProps> = ({
  hasTrial,
  priceString,
  period,
  trialDays,
  onRestore,
  isRestoring,
}) => {
  const openLink = async (url: string) => {
    try {
      const supported = await Linking.canOpenURL(url);
      if (supported) {
        await Linking.openURL(url);
      }
    } catch (error) {
      console.error('[LegalFooter] Failed to open link:', error);
    }
  };

  const periodText = period === 'annual' ? 'year' : 'month';

  return (
    <View style={styles.container}>
      {/* Restore purchases */}
      <Pressable
        onPress={onRestore}
        disabled={isRestoring}
        style={styles.restoreButton}
      >
        <Text variant="body2" style={styles.restoreText}>
          {isRestoring ? 'Restoring...' : 'Restore Purchases'}
        </Text>
      </Pressable>

      {/* Legal text */}
      <Text variant="caption" color="secondary" style={styles.legalText}>
        {hasTrial && trialDays ? (
          <>
            After your {trialDays}-day free trial, your subscription will automatically renew for {priceString}/{periodText} unless you cancel at least 24 hours before the end of the trial period.{' '}
          </>
        ) : (
          <>
            Your subscription will automatically renew for {priceString}/{periodText} unless you cancel at least 24 hours before the end of the current period.{' '}
          </>
        )}
        You can manage or cancel your subscription in your device's Account Settings. No refunds for partial periods.{'\n\n'}
        <Text
          variant="caption"
          style={styles.link}
          onPress={() => openLink(TERMS_URL)}
        >
          Terms of Service
        </Text>
        {' • '}
        <Text
          variant="caption"
          style={styles.link}
          onPress={() => openLink(PRIVACY_URL)}
        >
          Privacy Policy
        </Text>
      </Text>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    paddingTop: Theme.spacing.lg,
    paddingBottom: Theme.spacing.md,
  },
  restoreButton: {
    alignSelf: 'center',
    padding: Theme.spacing.sm,
    marginBottom: Theme.spacing.md,
  },
  restoreText: {
    color: Theme.colors.primary,
    fontWeight: '600',
  },
  legalText: {
    textAlign: 'center',
    lineHeight: 18,
  },
  link: {
    color: Theme.colors.primary,
    textDecorationLine: 'underline',
  },
});

